/**
 * The one clinical runtime: the browser talks to Supabase (a local Docker
 * stack in development, the staging branch, or production) through the AAL2
 * workforce gate. The former browser-local "synthetic" runtime was retired in
 * favour of the seeded local stack (`pnpm local:up`).
 */
export type ClinicalRuntimeConfig = {
  readonly mode: "supabase";
  readonly supabaseUrl: string;
  readonly supabasePublishableKey: string;
  readonly agentApiOrigin: string;
  readonly siteId: string;
  /**
   * Offers the persisted forms UI when VITE_CLINIC_DOCUMENT_VAULT_ENABLED is exactly
   * "true". The legacy staging flag is used only when the new flag is absent.
   * This public build setting grants no server or database authority.
   */
  readonly clinicDocumentVaultEnabled?: boolean;
  /**
   * Non-production only. True solely when VITE_WORKFORCE_MFA_OPTIONAL is exactly
   * "true"; absent or any other value keeps AAL2 workforce verification mandatory.
   */
  readonly mfaOptional?: boolean;
  /**
   * Registering a new patient from another EMR's chart page (EXECDEV-227), a
   * demo and onboarding tool: on in local development (Vite's dev server) and in
   * hosted staging builds (VITE_HOSTED_STAGING exactly "true"), off in every
   * other build, production included.
   */
  readonly emrPageImportEnabled?: boolean;
};

/**
 * Whether this build offers registering a patient from another EMR's chart
 * page: local development (Vite's dev server) and hosted staging builds only.
 * Read from the build alone, so it answers even where the runtime is not
 * configured.
 */
export function emrPageImportOffered(
  environment: Record<string, string | boolean | undefined> = import.meta.env,
): boolean {
  return environment.DEV === true || environment.VITE_HOSTED_STAGING === "true";
}

const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function loadClinicalRuntimeConfig(
  environment: Record<string, string | boolean | undefined> = import.meta.env,
): ClinicalRuntimeConfig {
  // The Horizon encounter was removed; a build still asking for it must not
  // quietly serve a different surface.
  if (environment.VITE_ENCOUNTER_UI === "horizon") {
    throw new Error("The Horizon encounter was removed; unset VITE_ENCOUNTER_UI");
  }
  const mode = environment.VITE_CLINICAL_RUNTIME;
  if (mode !== "supabase") {
    throw new Error(
      "VITE_CLINICAL_RUNTIME must be supabase: point the app at the local stack (pnpm local:up && pnpm local:env:activate) or a Supabase branch — see docs/local-development.md",
    );
  }

  const supabaseUrl = required(environment, "VITE_SUPABASE_URL");
  const supabasePublishableKey = required(environment, "VITE_SUPABASE_PUBLISHABLE_KEY");
  const agentApiOrigin = required(environment, "VITE_AGENT_API_ORIGIN");
  const siteId = required(environment, "VITE_CLINICAL_SITE_ID");
  if (!uuidPattern.test(siteId)) throw new Error("VITE_CLINICAL_SITE_ID must be a UUID");
  const api = new URL(agentApiOrigin, globalThis.location?.origin ?? "http://127.0.0.1");
  if (api.protocol !== "https:" && api.hostname !== "127.0.0.1" && api.hostname !== "localhost") {
    throw new Error("The clinical Agent API must use HTTPS outside local development");
  }
  return {
    mode,
    supabaseUrl,
    supabasePublishableKey,
    agentApiOrigin: api.toString(),
    siteId,
    clinicDocumentVaultEnabled:
      (environment.VITE_CLINIC_DOCUMENT_VAULT_ENABLED ??
        environment.VITE_CLINIC_DOCUMENT_VAULT_SYNTHETIC_STAGING) === "true",
    mfaOptional: environment.VITE_WORKFORCE_MFA_OPTIONAL === "true",
    emrPageImportEnabled: emrPageImportOffered(environment),
  };
}

function required(environment: Record<string, string | boolean | undefined>, name: string): string {
  const value = environment[name];
  if (typeof value !== "string" || value.length === 0) throw new Error(`${name} is required`);
  return value;
}
